import { Button, Dialog, Icon } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Form, FormRenderProps, useField } from 'react-final-form';
import { SellerField } from './SellerField';
import { VinField } from './VinField';
import { useHistory } from 'react-router-dom';
import { closeVinDialog, openCarInfoDialog, useVinDialogOpened } from '../../../../services/listerUploadVehicle/v2';
import {
  useGetVehicleByIdQuery,
  useGetVinInfoQueryState,
  useUpdateVehicleFieldMutation,
  useUploadVehicleMutation
} from '../../../../services/api/upload-vehicle';
import { snackShow } from 'actions';
import { useGetSellersQuery } from 'services/api';
import { AuctionField, auctionTypeOptions } from './AuctionField';
import { Vehicle } from 'types/vehicle';
import { useCurrentUser } from 'hooks/selectors';
import { useDeviceInfo } from 'hooks/useDeviceInfo';
import React from 'react';

const StyledDialog = styled(Dialog)`
  .create-vehicle-paper {
    margin: auto 13px;
    box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
    border-radius: 20px;
    width: 100%;
  }

  @media (min-width: 1200px) {
    .create-vehicle-paper {
      max-width: 367px;
    }
  }
`;

const Wrapper = styled.form`
  display: flex;
  gap: 24px;
  flex-direction: column;
  padding: 26px 24px 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.div`
  font-size: 22px;
  font-weight: 500;
  line-height: 25.78px;
  color: #000;
`;

const CloseIcon = styled(Icon)`
  width: 24px;
  height: 24px;
`;

const InputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: 19px;
`;

const Action = styled(Button)<{ disabled?: boolean }>`
  border-radius: 6px;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25) !important;
  font-size: 16px;
  font-weight: 500;
  line-height: 19px;
  padding-top: 13px;
  padding-bottom: 12px;
  text-transform: none;
`;

const CancelAction = styled(Action)<{ disabled?: boolean }>`
  background: rgba(217, 217, 217, 1);
  color: rgba(117, 117, 117, 1);
`;

const SubmitAction = styled(Action)<{ disabled?: boolean }>`
  background-color: ${props => (props.disabled ? 'rgba(34, 90, 145, 0.5)' : 'rgba(34, 90, 145, 1)')} !important;
  color: #fff !important;
`;

type FormValues = {
  seller_id: any;
  vin: string;
  manual_upload: boolean;
  auction_type: {
    label: string;
    value: string;
  };
  is_reupload: boolean;
};

const getAuctionTypeInitialValue = (vehicle?: Vehicle) => {
  if (!vehicle || !vehicle.auction_type) return auctionTypeOptions[0];
  return auctionTypeOptions.find(opt => opt.value === vehicle.auction_type) || auctionTypeOptions[0];
};

export const VinDialog = () => {
  const dispatch = useDispatch();

  const { vehicleId, isOpened, type, vin } = useVinDialogOpened();

  const user = useCurrentUser();

  const { data: vehicleData, refetch } = useGetVehicleByIdQuery(vehicleId!, { skip: !vehicleId });

  const vehicle = vehicleId ? vehicleData : undefined;

  const [updateVehicle] = useUpdateVehicleFieldMutation();
  const [uploadVehicle] = useUploadVehicleMutation();

  const { data: users = [] } = useGetSellersQuery(undefined, {
    skip: user.role !== 'admin'
  });

  const userOptions = users.map(u => ({
    value: u.id,
    label: u.dealership_name
  }));

  if (!vehicle && type === 'edit') {
    return null;
  }

  const initialSellerId = (() => {
    if (user.role === 'seller') {
      return user.id;
    }

    return vehicle ? userOptions.find(e => e.value === vehicle.user_id) : null;
  })();

  const initialValues = {
    seller_id: initialSellerId,
    vin: vin || vehicle?.vin || '',
    manual_upload: false,
    auction_type: getAuctionTypeInitialValue(vehicle),
    is_reupload: false
  };

  const history = useHistory();

  const close = () => {
    dispatch(closeVinDialog());
  };

  const openVinInfoDialog = () => {
    if (!vehicleId || !type) return;
    dispatch(openCarInfoDialog({ vehicleId, type }));
  };

  const { isMobile } = useDeviceInfo();

  const handleUpload = async (vin: string, seller_id: number, manual_upload: boolean, auction_type: string) => {
    const response = await uploadVehicle({
      vin,
      seller_id,
      upload_type: manual_upload ? 'manual' : 'automatic',
      auction_type
    });
    if ('data' in response) {
      close();
      if (isMobile) {
        dispatch(openCarInfoDialog({ vehicleId: response.data.id, type: type! }));
      } else {
        history.push(`/vehicles/${response.data.id}/review`);
      }
    } else {
      dispatch(snackShow({ type: 'error', message: 'Error while saving vehicle' }));
    }
  };

  const handleEdit = async (vin: string, seller_id: number, auction_type: string) => {
    if (!vehicleId) return;
    const response = await updateVehicle({
      id: vehicleId,
      payload: {
        seller_id,
        vin,
        auction_type
      }
    });
    if ('error' in response) {
      dispatch(snackShow({ type: 'error', message: 'Error while saving vehicle' }));
    } else {
      await refetch();
      close();
      openVinInfoDialog();
    }
  };

  const handleSubmit = async (values: FormValues, form: any) => {
    const { dirty } = form.getState();
    if (!dirty) {
      close();
      openVinInfoDialog();
      return;
    }

    if (type !== 'edit') {
      return await handleUpload(values.vin, values.seller_id?.value, values.manual_upload, values.auction_type.value);
    }

    await handleEdit(values.vin, values.seller_id?.value, values.auction_type.value);
  };

  return (
    <StyledDialog classes={{ paper: 'create-vehicle-paper' }} open={isOpened} onBackdropClick={close}>
      <Form keepDirtyOnReinitialize onSubmit={handleSubmit} initialValues={initialValues}>
        {props => {
          return <VinDialogForm {...props} />;
        }}
      </Form>
    </StyledDialog>
  );
};

type FormProps = FormRenderProps<FormValues>;

const VinDialogForm: React.FC<FormProps> = props => {
  const dispatch = useDispatch();

  const { vehicleId, type } = useVinDialogOpened();

  const user = useCurrentUser();
  const title = user.job_title === 'Lister' ? 'Please input VIN and choose the store' : 'Please input VIN';

  const { data: users = [] } = useGetSellersQuery(undefined, {
    skip: user.role !== 'admin'
  });

  const userOptions = users.map(u => ({
    value: u.id,
    label: u.dealership_name
  }));

  const history = useHistory();

  const close = () => {
    dispatch(closeVinDialog());
  };

  const closeWithRedirect = () => {
    close();
    if (type === 'edit') {
      history.push(`/lister/upload-vehicle/v2/${vehicleId}`);
    }
  };

  const {
    input: { value: formVin },
    meta: { error }
  } = useField('vin');

  const { isFetching } = useGetVinInfoQueryState(formVin!, {
    skip: type === 'edit' || !formVin || !!error
  });

  const submitDisabled = isFetching || props.submitting || !props.values.seller_id || !props.values.vin;
  const submitButtonText = (() => {
    if (props.values.is_reupload) return 'REUPLOAD';
    return props.values.manual_upload ? 'Upload Manually' : 'Save';
  })();

  return (
    <Wrapper onSubmit={props.handleSubmit}>
      <Header>
        <Title>{title}</Title>
        <CloseIcon onClick={closeWithRedirect}>close</CloseIcon>
      </Header>
      <InputsWrapper>
        {user.role !== 'seller' && <SellerField userOptions={userOptions} />}
        <VinField type={type!} />
        {user.role !== 'seller' && <AuctionField />}
      </InputsWrapper>
      <ActionsWrapper>
        <CancelAction fullWidth variant="contained" type="button" onClick={close}>
          Cancel
        </CancelAction>
        <SubmitAction disabled={submitDisabled} fullWidth variant="contained" type="submit">
          {submitButtonText}
        </SubmitAction>
      </ActionsWrapper>
    </Wrapper>
  );
};
